import { useEffect, useRef } from 'react';
import { compareAsc } from 'date-fns';
import type { ApolloError } from '@apollo/client';
import type { GetUnopenedInboxItemCountQuery } from '@frameio/graphql-client/generated';
import {
  GetUnopenedInboxItemCount,
  useInboxUnopenedCountEventSubscription,
} from '@frameio/graphql-client/generated';
import { reportError } from '@frameio/telemetry';

type InboxUnopenedCountSubscriptionProps = {
  accountId: string;
};

export const InboxUnopenedCountSubscription = ({
  accountId,
}: InboxUnopenedCountSubscriptionProps) => {
  const lastTimestampRef = useRef<Date | null>(null);
  const prevAccountIdRef = useRef<string | null>(null);

  // Reset the timestamp guard when switching accounts
  useEffect(() => {
    if (!accountId) return;

    if (
      prevAccountIdRef.current !== null &&
      prevAccountIdRef.current !== accountId
    ) {
      lastTimestampRef.current = null;
    }

    prevAccountIdRef.current = accountId;
  }, [accountId]);

  useInboxUnopenedCountEventSubscription({
    variables: {
      scope: {
        unopenedCountUpdate: { accountId },
      },
    },
    skip: !accountId,
    onData: async ({ client, data }) => {
      const payload = data?.data?.inbox;

      if (!payload) return;

      const incomingTimestamp = new Date(payload.timestamp ?? '');

      if (
        lastTimestampRef.current &&
        compareAsc(lastTimestampRef.current, incomingTimestamp) !== -1
      ) {
        return;
      }

      lastTimestampRef.current = incomingTimestamp;

      const existingData =
        client.cache.readQuery<GetUnopenedInboxItemCountQuery>({
          query: GetUnopenedInboxItemCount,
          variables: { accountId },
        });

      if (existingData?.me?.__typename !== 'AuthenticatedUser') return;

      client.cache.writeQuery<GetUnopenedInboxItemCountQuery>({
        query: GetUnopenedInboxItemCount,
        variables: { accountId },
        data: {
          me: {
            ...existingData.me,
            inbox: {
              __typename: 'UserInbox',
              id: existingData.me.inbox?.id ?? null,
              items: {
                __typename: 'InboxItemConnection',
                totalCount: payload.unopenedCount ?? 0,
              },
            },
          },
        },
      });

      await client.refetchQueries({ include: ['GetInboxItems'] });
    },
    onError: (error: ApolloError) => {
      reportError(error, {
        message: 'Inbox unopened count subscription error',
        accountId,
      });
    },
  });

  return null;
};
